import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import AdminService from '../../../services/adminService';
import aiProviderAdminService from '../../../services/aiProviderAdminService';

const numberFmt = (n) => {
  if (n === null || n === undefined) return '0';
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString() : String(n);
};

const percentFmt = (p) => {
  if (p === null || p === undefined) return '0%';
  const v = Number(p);
  return Number.isFinite(v) ? `${v}%` : String(p);
};

const Bar = ({ value = 0, max = 100, color = 'bg-blue-600' }) => {
  const pct = Math.max(0, Math.min(100, max ? (value / max) * 100 : 0));
  return (
    <div className="w-full h-2 bg-gray-700 rounded">
      <div className={`h-2 ${color} rounded`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const AnalyticsDashboard = () => {
  const { t } = useLanguage();
  const [rangeDays, setRangeDays] = useState(30);
  const [wizard, setWizard] = useState({
    totalSessions: 0,
    activeSessions: 0,
    completedSessions: 0,
    averageCompletionMinutes: 0,
    dropOffRate: 0,
    stepDistribution: {}
  });
  const [subs, setSubs] = useState({
    totalSubmissions: 0,
    statusDistribution: {},
    pathwayDistribution: {},
    leadSources: {},
    conversionMetrics: {},
    recentSubmissions: []
  });
  const [aiStats, setAiStats] = useState({
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    totalTokens: 0,
    totalCost: 0,
    byProvider: {},
    byTask: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAnalytics = useCallback(async (days) => {
    try {
      setLoading(true);
      setError(null);
      const [wiz, subStats, ai] = await Promise.all([
        AdminService.getWizardAnalytics(days),
        AdminService.getSubmissionStats(days),
        aiProviderAdminService.getUsageStatistics(days)
      ]);
      setWizard(wiz || {});
      setSubs(subStats || {});
      setAiStats(ai && ai.success && ai.data ? ai.data : {
        totalRequests: 0,
        successfulRequests: 0,
        failedRequests: 0,
        totalTokens: 0,
        totalCost: 0,
        byProvider: {},
        byTask: {}
      });
    } catch (e) {
      console.error('Analytics load error', e);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalytics(rangeDays);
  }, [loadAnalytics, rangeDays]);

  const sortedStepDistribution = useMemo(() => {
    const entries = Object.entries(wizard.stepDistribution || {});
    return entries
      .map(([step, count]) => ({ step: Number(step), count }))
      .sort((a, b) => a.step - b.step);
  }, [wizard.stepDistribution]);

  const topPathways = useMemo(() => {
    const entries = Object.entries(subs.pathwayDistribution || {});
    return entries.sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [subs.pathwayDistribution]);

  const topSources = useMemo(() => {
    const entries = Object.entries(subs.leadSources || {});
    return entries.sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [subs.leadSources]);

  const conversionMetrics = subs.conversionMetrics || {};

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-page text-white font-mono min-h-screen bg-gray-900">
      {/* Header */}
      <div className="admin-header">
        <h2 className="admin-title">
          {t('admin.analytics.title', 'Analytics Dashboard')}
        </h2>
        <p className="admin-subtitle">
          {t('admin.analytics.subtitle', 'Wizard funnel, submissions, and AI usage')}
        </p>

        {/* Error */}
        {error && (
          <div className="admin-alert admin-alert-error mt-4">
            <p>{error}</p>
          </div>
        )}

        <div className="admin-actions mt-4">
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            {t('admin.analytics.timeRange', 'Time range')}
          </label>
          <select
            value={rangeDays}
            onChange={(event) => setRangeDays(Number(event.target.value))}
            className="admin-select ml-2 bg-gray-800 border-gray-700 text-white"
          >
            <option value={7}>{t('admin.analytics.last7Days', 'Last 7 days')}</option>
            <option value={30}>{t('admin.analytics.last30Days', 'Last 30 days')}</option>
            <option value={90}>{t('admin.analytics.last90Days', 'Last 90 days')}</option>
            <option value={180}>{t('admin.analytics.last180Days', 'Last 180 days')}</option>
          </select>
        </div>
      </div>

      {/* Top metrics */}
      <div className="admin-grid-4">
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-content text-center">
            <div className="text-2xl font-bold text-blue-600">{numberFmt(wizard.totalSessions)}</div>
            <div className="text-sm text-gray-400 mt-1">Total Sessions</div>
          </div>
        </div>
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-content text-center">
            <div className="text-2xl font-bold text-green-600">{percentFmt(wizard.dropOffRate)}</div>
            <div className="text-sm text-gray-400 mt-1">Drop-off Rate</div>
          </div>
        </div>
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-content text-center">
            <div className="text-2xl font-bold text-purple-600">{numberFmt(subs.totalSubmissions)}</div>
            <div className="text-sm text-gray-400 mt-1">Submissions</div>
          </div>
        </div>
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-content text-center">
            <div className="text-2xl font-bold text-orange-600">{numberFmt(aiStats.totalCost)}</div>
            <div className="text-sm text-gray-400 mt-1">AI Cost (est.)</div>
          </div>
        </div>
      </div>

      {/* Wizard funnel snapshot */}
      <div className="admin-grid-2">
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-header">
            <h3 className="text-lg font-medium">Wizard Funnel</h3>
          </div>
          <div className="admin-card-content">
            <div className="admin-grid-2 gap-4">
              <div>
                <p className="text-sm text-gray-400">Active Sessions</p>
                <p className="text-lg font-semibold text-blue-600">{numberFmt(wizard.activeSessions)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Completed Sessions</p>
                <p className="text-lg font-semibold text-emerald-600">{numberFmt(wizard.completedSessions)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Average Completion</p>
                <p className="text-lg font-semibold">
                  {wizard.averageCompletionMinutes ? `${wizard.averageCompletionMinutes} min` : '—'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Drop-off Rate</p>
                <p className="text-lg font-semibold text-red-600">{percentFmt(wizard.dropOffRate)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-header">
            <h3 className="text-lg font-medium">Exit Step Distribution</h3>
          </div>
          <div className="admin-card-content">
            <div className="space-y-3">
              {sortedStepDistribution.length > 0 ? (
                sortedStepDistribution.map(({ step, count }) => (
                  <div key={step} className="flex items-center gap-4">
                    <div className="w-20 text-sm text-gray-400">Step {step}</div>
                    <div className="flex-1">
                      <Bar value={count} max={sortedStepDistribution[0].count} color="bg-blue-500" />
                    </div>
                    <div className="w-12 text-right text-sm text-gray-400">{numberFmt(count)}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-400">No exit data available for this range.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status distribution */}
      <div className="admin-card bg-gray-800 border-gray-700">
        <div className="admin-card-header">
          <h3 className="text-lg font-medium">Submission Status</h3>
        </div>
        <div className="admin-card-content">
          <div className="space-y-3">
            {Object.entries(subs.statusDistribution || {}).map(([status, count], index, arr) => {
              const max = arr.reduce((maxVal, [, value]) => Math.max(maxVal, value), 1);
              return (
                <div key={status} className="flex items-center gap-4">
                  <div className="w-32 text-sm text-gray-300 capitalize">{status}</div>
                  <div className="flex-1">
                    <Bar value={count} max={max} />
                  </div>
                  <div className="w-12 text-right text-sm text-gray-400">{numberFmt(count)}</div>
                </div>
              );
            })}
            {Object.keys(subs.statusDistribution || {}).length === 0 && (
              <div className="text-sm text-gray-400">No submissions yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Top pathways & sources */}
      <div className="admin-grid-2">
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-header">
            <h3 className="text-lg font-medium">Top Pathways</h3>
          </div>
          <div className="admin-card-content">
            <div className="space-y-3">
              {topPathways.length > 0 ? topPathways.map(([pathway, count]) => (
                <div key={pathway} className="flex items-center gap-4">
                  <div className="flex-1 text-sm text-gray-400 truncate">{pathway}</div>
                  <div className="w-12 text-right text-sm text-gray-400">{numberFmt(count)}</div>
                </div>
              )) : (
                <div className="text-sm text-gray-400">No pathways recorded in this range.</div>
              )}
            </div>
          </div>
        </div>

        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-header">
            <h3 className="text-lg font-medium">Lead Sources</h3>
          </div>
          <div className="admin-card-content">
            <div className="space-y-3">
              {topSources.length > 0 ? topSources.map(([source, count]) => (
                <div key={source} className="flex items-center gap-4">
                  <div className="flex-1 text-sm text-gray-400 truncate">{source}</div>
                  <div className="w-12 text-right text-sm text-gray-400">{numberFmt(count)}</div>
                </div>
              )) : (
                <div className="text-sm text-gray-400">No lead sources captured.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Conversion snapshot */}
      <div className="admin-grid-4">
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-content text-center">
            <p className="text-sm text-gray-400">Converted</p>
            <p className="text-2xl font-semibold text-emerald-600">{numberFmt(conversionMetrics.converted || 0)}</p>
          </div>
        </div>
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-content text-center">
            <p className="text-sm text-gray-400">In Pipeline</p>
            <p className="text-2xl font-semibold text-blue-600">{numberFmt(conversionMetrics.in_pipeline || 0)}</p>
          </div>
        </div>
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-content text-center">
            <p className="text-sm text-gray-400">Lost</p>
            <p className="text-2xl font-semibold text-red-600">{numberFmt(conversionMetrics.lost || 0)}</p>
          </div>
        </div>
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-content text-center">
            <p className="text-sm text-gray-400">Conversion Rate</p>
            <p className="text-2xl font-semibold text-indigo-600">{percentFmt(conversionMetrics.conversion_rate)}</p>
          </div>
        </div>
      </div>

      {/* AI provider usage */}
      <div className="admin-card bg-gray-800 border-gray-700">
        <div className="admin-card-header">
          <h3 className="text-lg font-medium">AI Usage (last {rangeDays} days)</h3>
        </div>
        <div className="admin-card-content">
          <div className="admin-grid-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{numberFmt(aiStats.totalRequests)}</div>
              <div className="text-xs text-gray-400">Requests</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-700">{numberFmt(aiStats.successfulRequests)}</div>
              <div className="text-xs text-gray-400">Successful</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-700">{numberFmt(aiStats.failedRequests)}</div>
              <div className="text-xs text-gray-400">Failed</div>
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(aiStats.byProvider || {}).map(([provider, stats]) => {
              const max = Object.values(aiStats.byProvider || {}).reduce((maxVal, { requests }) => Math.max(maxVal, requests), 1);
              return (
                <div key={provider} className="flex items-center gap-4">
                  <div className="w-32 text-sm text-gray-300">{provider}</div>
                  <div className="flex-1">
                    <Bar value={stats.requests} max={max} color="bg-indigo-600" />
                  </div>
                  <div className="w-12 text-right text-sm text-gray-400">{numberFmt(stats.requests)}</div>
                </div>
              );
            })}
            {Object.keys(aiStats.byProvider || {}).length === 0 && (
              <div className="text-sm text-gray-400">No AI usage logged.</div>
            )}
          </div>
        </div>
      </div>

      {/* Recent submissions */}
      <div className="admin-card bg-gray-800 border-gray-700">
        <div className="admin-card-header">
          <h3 className="text-lg font-medium">Recent Submissions</h3>
        </div>
        <div className="admin-card-content p-0">
          <ul className="divide-y divide-gray-700">
            {(subs.recentSubmissions || []).map((s) => (
              <li key={s.id} className="px-6 py-3 flex items-center justify-between">
                <div className="text-sm text-gray-200">
                  {s.full_name || s.personal_info?.fullName || s.personal_info?.name || s.id}
                  <span className="ml-2 text-xs text-gray-400 uppercase">{s.status || 'unknown'}</span>
                </div>
                <div className="flex items-center gap-3">
                  {s.lead_source && <span className="text-xs text-gray-500 uppercase">{s.lead_source}</span>}
                  <div className="text-xs text-gray-400">{new Date(s.created_at).toLocaleString()}</div>
                </div>
              </li>
            ))}
            {(subs.recentSubmissions || []).length === 0 && (
              <li className="px-6 py-3 text-sm text-gray-400">No recent submissions.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;

